import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Info, Loader2, ChevronLeft, ChevronRight, X, RefreshCw, Search, Download, Send,
} from 'lucide-react';
import {
  useWorkflowQuery,
  useWorkflowEmailLogsQuery,
  useRetryFailedEmailsMutation,
  type WorkflowEmailLog,
} from '@/hooks/useWorkflows';
import ConfirmModal from '@/components/ConfirmModal';
import { STORAGE, API_URL } from '@/_config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(val: string | null | undefined): string {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateShort(val: string | null | undefined): string {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'sent':        return 'wel-badge wel-badge--green';
    case 'sending':
    case 'queued':      return 'wel-badge wel-badge--sky';
    case 'pending':     return 'wel-badge wel-badge--amber';
    case 'failed':
    case 'bounced':
    case 'complained':  return 'wel-badge wel-badge--red';
    case 'skipped':
    case 'unsubscribed':return 'wel-badge wel-badge--gray';
    default:            return 'wel-badge wel-badge--gray';
  }
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// ---------------------------------------------------------------------------
// Status tabs
// ---------------------------------------------------------------------------

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'pending', label: 'Pending' },
  { key: 'failed', label: 'Failed' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
];

// ---------------------------------------------------------------------------
// Pagination helper
// ---------------------------------------------------------------------------

function getPageNumbers(current: number, last: number): (number | '…')[] {
  if (last <= 1) return [1];
  const delta = 2;
  const range: number[] = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(last - 1, current + delta); i++) {
    range.push(i);
  }
  const pages: (number | '…')[] = [1];
  if (range.length > 0 && range[0] > 2) pages.push('…');
  pages.push(...range);
  if (range.length > 0 && range[range.length - 1] < last - 1) pages.push('…');
  if (last > 1) pages.push(last);
  return pages;
}

// ---------------------------------------------------------------------------
// Details Modal
// ---------------------------------------------------------------------------

function DetailsModal({ log, onClose }: { log: WorkflowEmailLog; onClose: () => void }) {
  return (
    <div className="wel-modal-overlay" onClick={onClose}>
      <div className="wel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wel-modal-header">
          <h3 className="wel-modal-title">Email Details</h3>
          <button className="wel-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="wel-modal-body">
          <div className="wel-detail-grid">
            <div className="wel-detail-row">
              <span className="wel-detail-label">Scheduled at</span>
              <span className="wel-detail-value">{formatDate(log.scheduled_for)}</span>
            </div>
            <div className="wel-detail-row">
              <span className="wel-detail-label">Sent at</span>
              <span className="wel-detail-value">{formatDate(log.sent_at)}</span>
            </div>
            {log.unsubscribed_at && (
              <div className="wel-detail-row">
                <span className="wel-detail-label">Unsubscribed at</span>
                <span className="wel-detail-value">{formatDate(log.unsubscribed_at)}</span>
              </div>
            )}
            {log.failure_reason && (
              <div className="wel-detail-row wel-detail-row--full">
                <span className="wel-detail-label">Failure reason</span>
                <span className="wel-detail-value wel-detail-value--danger">{log.failure_reason}</span>
              </div>
            )}
            {log.status !== 'unsubscribed' && (
              <div className="wel-detail-row wel-detail-row--full">
                <span className="wel-detail-label">Message ID</span>
                <span className="wel-detail-value wel-detail-value--mono">{log.message_id ?? '—'}</span>
              </div>
            )}
            <div className="wel-detail-row">
              <span className="wel-detail-label">DateTime (created)</span>
              <span className="wel-detail-value">{formatDate(log.created_at)}</span>
            </div>
            <div className="wel-detail-row">
              <span className="wel-detail-label">Step</span>
              <span className="wel-detail-value">Step {log.step_number}</span>
            </div>
            <div className="wel-detail-row">
              <span className="wel-detail-label">Variant</span>
              <span className="wel-detail-value">{log.variant_label}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Preview Modal
// ---------------------------------------------------------------------------

function EmailPreviewModal({ log, onClose }: { log: WorkflowEmailLog; onClose: () => void }) {
  return (
    <div className="wel-modal-overlay" onClick={onClose}>
      <div className="wel-modal wel-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="wel-modal-header">
          <div>
            <h3 className="wel-modal-title">{log.subject || '(No subject)'}</h3>
            <p className="wel-modal-subtitle">
              Step {log.step_number} &middot; Variant {log.variant_label}
            </p>
          </div>
          <button className="wel-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="wel-modal-body">
          <div
            className="wel-email-preview"
            dangerouslySetInnerHTML={{ __html: log.email_body }}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function WorkflowEmailLogsPage() {
  const { id } = useParams<{ id: string }>();
  const [activeStatus, setActiveStatus] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  function handlePerPageChange(val: number) {
    setPerPage(val);
    setPage(1);
  }
  const [detailsLog, setDetailsLog] = useState<WorkflowEmailLog | null>(null);
  const [previewLog, setPreviewLog] = useState<WorkflowEmailLog | null>(null);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const retryFailed = useRetryFailedEmailsMutation();

  // Advanced filter raw values (bound to inputs)
  const [leadFilter, setLeadFilter]       = useState('');
  const [senderFilter, setSenderFilter]   = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  // Debounced values sent to the API (300 ms)
  const [dLead, setDLead]       = useState('');
  const [dSender, setDSender]   = useState('');
  const [dCompany, setDCompany] = useState('');

  const leadRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const senderRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (leadRef.current) clearTimeout(leadRef.current);
    leadRef.current = setTimeout(() => { setDLead(leadFilter); setPage(1); }, 300);
    return () => { if (leadRef.current) clearTimeout(leadRef.current); };
  }, [leadFilter]);

  useEffect(() => {
    if (senderRef.current) clearTimeout(senderRef.current);
    senderRef.current = setTimeout(() => { setDSender(senderFilter); setPage(1); }, 300);
    return () => { if (senderRef.current) clearTimeout(senderRef.current); };
  }, [senderFilter]);

  useEffect(() => {
    if (companyRef.current) clearTimeout(companyRef.current);
    companyRef.current = setTimeout(() => { setDCompany(companyFilter); setPage(1); }, 300);
    return () => { if (companyRef.current) clearTimeout(companyRef.current); };
  }, [companyFilter]);

  const hasActiveFilters = !!(dLead || dSender || dCompany || activeStatus);

  const [isExporting, setIsExporting] = useState(false);

  async function handleExport() {
    if (isExporting || total === 0) return;
    setIsExporting(true);
    try {
      const raw = localStorage.getItem(STORAGE);
      const token: string | undefined = raw
        ? (JSON.parse(raw) as any)?.access_token ?? (JSON.parse(raw) as any)?.token
        : undefined;

      const params = new URLSearchParams();
      if (activeStatus) params.set('status',         activeStatus);
      if (dLead)        params.set('search_lead',    dLead);
      if (dSender)      params.set('search_sender',  dSender);
      if (dCompany)     params.set('search_company', dCompany);

      const exportUrl = `${API_URL.WORKFLOWS.EMAIL_LOGS_EXPORT(id!)}${params.size ? '?' + params.toString() : ''}`;
      const response = await fetch(exportUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return;

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] ?? `workflow-emails-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // silently ignore export errors
    } finally {
      setIsExporting(false);
    }
  }

  const { data: workflow } = useWorkflowQuery(id);

  const { data, isLoading, isFetching, refetch } = useWorkflowEmailLogsQuery(id, {
    status: activeStatus || undefined,
    page,
    per_page: perPage,
    search_lead: dLead || undefined,
    search_sender: dSender || undefined,
    search_company: dCompany || undefined,
  });

  const logs = data?.data?.logs ?? [];
  const summary = data?.data?.summary;

  // The backend meta field carries: { total, page, per_page, last_page }
  const responseMeta = data?.meta as unknown as { total?: number; page?: number; per_page?: number; last_page?: number } | null;
  const total = responseMeta?.total ?? summary?.total ?? 0;
  const lastPage = responseMeta?.last_page ?? Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  function handleTabChange(key: string) {
    setActiveStatus(key);
    setPage(1);
  }

  function handleResendFailed() {
    if (!id) return;
    retryFailed.mutate(id, {
      onSuccess: (res) => {
        const count = res?.data?.retried ?? 0;
        toast.success('Failed emails queued for resend', {
          description: `${count} email${count === 1 ? '' : 's'} moved back to the pending queue.`,
        });
        setShowResendConfirm(false);
      },
      onError: () => {
        toast.error('Failed to resend emails. Please try again.');
        setShowResendConfirm(false);
      },
    });
  }

  return (
    <div className="wel-page">
      {/* Header */}
      <div className="wel-header">
        <Link to="/app/workflows" className="wel-back-link">
          <ArrowLeft size={16} /> Back to Workflows
        </Link>
        <div className="wel-header-title-row">
          <h1 className="wel-title">
            <Mail size={20} color="var(--navy)" />
            {workflow?.data?.title ?? 'Workflow'} — Email Logs
          </h1>
          <div className="wel-header-right">
            {summary && (
              <div className="wel-summary-chips">
                <span className="wel-chip wel-chip--green">{summary.sent} Sent</span>
                <span className="wel-chip wel-chip--amber">{summary.pending} Pending</span>
                <span className="wel-chip wel-chip--red">{summary.failed} Failed</span>
                <span className="wel-chip wel-chip--gray">{summary.total} Total</span>
              </div>
            )}
            <button
              className="btn btn-secondary btn-sm wel-export-btn"
              onClick={handleExport}
              disabled={total === 0 || isExporting}
              title={total === 0 ? 'No data to export' : 'Export visible data as CSV'}
            >
              <Download size={14} />
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button
              className="btn btn-secondary btn-sm wel-refresh-btn"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh email logs"
            >
              <RefreshCw size={14} className={isFetching ? 'wel-spin' : ''} />
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
            {activeStatus === 'failed' && (
              <button
                className="btn btn-sm wel-resend-btn"
                onClick={() => setShowResendConfirm(true)}
                disabled={(summary?.failed ?? 0) === 0 || retryFailed.isPending}
                title={(summary?.failed ?? 0) === 0 ? 'No failed emails to retry' : 'Retry all failed emails'}
              >
                <Send size={14} />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="wel-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`wel-tab${activeStatus === tab.key ? ' wel-tab--active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Advanced filters */}
      <div className="wel-filters">
        <div className="wel-filter-input-wrap">
          <Search size={13} className="wel-filter-icon" />
          <input
            className="wel-filter-input"
            type="text"
            placeholder="Lead Name or Email"
            value={leadFilter}
            onChange={(e) => setLeadFilter(e.target.value)}
          />
          {leadFilter && (
            <button className="wel-filter-clear" onClick={() => setLeadFilter('')} title="Clear"><X size={11} /></button>
          )}
        </div>
        <div className="wel-filter-input-wrap">
          <Search size={13} className="wel-filter-icon" />
          <input
            className="wel-filter-input"
            type="text"
            placeholder="Company Name"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
          />
          {companyFilter && (
            <button className="wel-filter-clear" onClick={() => setCompanyFilter('')} title="Clear"><X size={11} /></button>
          )}
        </div>
        <div className="wel-filter-input-wrap">
          <Search size={13} className="wel-filter-icon" />
          <input
            className="wel-filter-input"
            type="text"
            placeholder="Sender Email"
            value={senderFilter}
            onChange={(e) => setSenderFilter(e.target.value)}
          />
          {senderFilter && (
            <button className="wel-filter-clear" onClick={() => setSenderFilter('')} title="Clear"><X size={11} /></button>
          )}
        </div>
        {(leadFilter || companyFilter || senderFilter) && (
          <button
            className="btn btn-secondary btn-sm wel-filter-reset"
            onClick={() => { setLeadFilter(''); setCompanyFilter(''); setSenderFilter(''); }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card wel-table-card">
        {isLoading ? (
          <div className="wel-loading">
            <Loader2 size={24} className="wel-spin" />
            <span>Loading email logs…</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="wel-empty">
            <Mail size={36} color="var(--gray-300)" />
            <p className="wel-empty-title">No emails found</p>
            <p className="wel-empty-sub">
              {hasActiveFilters ? 'No emails match the current filters.' : 'No emails have been sent for this workflow yet.'}
            </p>
          </div>
        ) : (
          <table className="wel-table">
            <thead>
              <tr className="wel-thead-row">
                <th className="wel-th">Name</th>
                <th className="wel-th">From Email</th>
                <th className="wel-th">Company</th>
                <th className="wel-th">Status</th>
                <th className="wel-th">Scheduled At</th>
                {activeStatus === 'unsubscribed' && <th className="wel-th">Unsubscribed At</th>}
                <th className="wel-th wel-th--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="wel-row">
                  <td className="wel-td">
                    <div className="wel-name-cell">
                      <div className="wel-avatar">{getInitials(log.to_name)}</div>
                      <div className="wel-name-info">
                        <span className="wel-name">{log.to_name ?? '—'}</span>
                        <span className="wel-email-sub">{log.to_email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="wel-td wel-td--secondary">{log.from_email}</td>
                  <td className="wel-td wel-td--secondary">{log.company ?? '—'}</td>
                  <td className="wel-td">
                    <span className={statusBadgeClass(log.status)}>{statusLabel(log.status)}</span>
                  </td>
                  <td className="wel-td wel-td--secondary">{formatDateShort(log.scheduled_for)}</td>
                  {activeStatus === 'unsubscribed' && (
                    <td className="wel-td wel-td--secondary">{formatDateShort(log.unsubscribed_at)}</td>
                  )}
                  <td className="wel-td wel-td--actions">
                    <button
                      className="wel-icon-btn"
                      title="View details (scheduled at, sent at, failure reason, message ID)"
                      onClick={() => setDetailsLog(log)}
                    >
                      <Info size={15} />
                    </button>
                    <button
                      className="wel-icon-btn"
                      title="Preview email (subject, body, step, variant)"
                      onClick={() => setPreviewLog(log)}
                    >
                      <Mail size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <div className="wel-pagination">
          <div className="wel-pagination-left">
            <span className="wel-pagination-info">Showing {from}–{to} of {total} emails</span>
            <label className="wel-per-page-label">
              Rows per page:
              <select
                className="wel-per-page-select"
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
              >
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <div className="wel-pagination-controls">
            <button
              className="btn btn-secondary btn-sm wel-page-arrow"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            {getPageNumbers(page, lastPage).map((p, i) =>
              p === '…'
                ? <span key={`ellipsis-${i}`} className="wel-page-ellipsis">…</span>
                : <button
                    key={p}
                    className={`wel-page-btn${page === p ? ' wel-page-btn--active' : ''}`}
                    onClick={() => setPage(p as number)}
                  >{p}</button>
            )}
            <button
              className="btn btn-secondary btn-sm wel-page-arrow"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {detailsLog && <DetailsModal log={detailsLog} onClose={() => setDetailsLog(null)} />}
      {previewLog && <EmailPreviewModal log={previewLog} onClose={() => setPreviewLog(null)} />}
      {showResendConfirm && (
        <ConfirmModal
          title="Resend Failed Emails"
          message="Are you sure you want to resend all failed emails for this workflow? This action will move the failed emails back to the pending queue for processing."
          confirmLabel="Confirm Resend"
          danger={false}
          loading={retryFailed.isPending}
          onCancel={() => setShowResendConfirm(false)}
          onConfirm={handleResendFailed}
        />
      )}

      <style>{`
        .wel-page {
          padding: 28px 32px;
          max-width: 1280px;
          margin: 0 auto;
        }

        /* Header */
        .wel-header { margin-bottom: 24px; }
        .wel-back-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--gray-500);
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          margin-bottom: 14px;
          transition: color 0.15s;
        }
        .wel-back-link:hover { color: var(--navy); }
        .wel-header-title-row {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .wel-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 22px;
          font-weight: 700;
          color: var(--navy);
          margin: 0;
        }

        /* Header right group */
        .wel-header-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .wel-refresh-btn  { display: inline-flex; align-items: center; gap: 6px; }
        .wel-export-btn   { display: inline-flex; align-items: center; gap: 6px; }
        .wel-resend-btn   { display: inline-flex; align-items: center; gap: 6px; background: var(--red, #ef4444); color: #fff; border: none; }
        .wel-resend-btn:hover:not(:disabled) { background: #dc2626; }
        .wel-resend-btn:disabled { opacity: 0.55; }

        /* Summary chips */
        .wel-summary-chips { display: flex; gap: 8px; flex-wrap: wrap; }
        .wel-chip {
          font-size: 12px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 999px;
        }
        .wel-chip--green  { background: var(--green-light);  color: var(--green);  }
        .wel-chip--amber  { background: var(--amber-light);  color: #b07800;       }
        .wel-chip--red    { background: #fde8e8;             color: var(--red);    }
        .wel-chip--gray   { background: var(--gray-100);     color: var(--gray-600); }

        /* Tabs */
        .wel-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1.5px solid var(--gray-200);
          margin-bottom: 20px;
        }
        .wel-tab {
          background: none;
          border: none;
          padding: 8px 16px;
          font-size: 13px;
          font-weight: 500;
          color: var(--gray-500);
          cursor: pointer;
          border-bottom: 2.5px solid transparent;
          margin-bottom: -1.5px;
          transition: color 0.15s, border-color 0.15s;
          border-radius: 0;
        }
        .wel-tab:hover { color: var(--navy); }
        .wel-tab--active {
          color: var(--navy);
          border-bottom-color: var(--navy);
          font-weight: 600;
        }

        /* Advanced filters */
        .wel-filters {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }
        .wel-filter-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 160px;
          max-width: 240px;
        }
        .wel-filter-icon {
          position: absolute;
          left: 9px;
          color: var(--gray-400);
          pointer-events: none;
          flex-shrink: 0;
        }
        .wel-filter-input {
          width: 100%;
          padding: 7px 28px 7px 28px;
          font-size: 13px;
          border: 1px solid var(--gray-200);
          border-radius: 7px;
          color: var(--gray-800);
          background: #fff;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .wel-filter-input::placeholder { color: var(--gray-400); }
        .wel-filter-input:focus {
          border-color: var(--navy);
          box-shadow: 0 0 0 3px rgba(var(--navy-rgb, 30,41,59), 0.08);
        }
        .wel-filter-clear {
          position: absolute;
          right: 7px;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--gray-400);
          display: flex;
          align-items: center;
          padding: 2px;
          border-radius: 4px;
          transition: color 0.15s;
        }
        .wel-filter-clear:hover { color: var(--navy); }
        .wel-filter-reset { white-space: nowrap; }

        /* Table card */
        .wel-table-card { padding: 0; overflow: hidden; }
        .wel-table { width: 100%; border-collapse: collapse; }
        .wel-thead-row { background: var(--gray-50); }
        .wel-th {
          padding: 11px 16px;
          text-align: left;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--gray-500);
          border-bottom: 1px solid var(--gray-200);
          white-space: nowrap;
        }
        .wel-th--actions { text-align: center; }
        .wel-row { transition: background 0.1s; }
        .wel-row:hover { background: var(--gray-50); }
        .wel-row:not(:last-child) { border-bottom: 1px solid var(--gray-100); }
        .wel-td {
          padding: 12px 16px;
          font-size: 13.5px;
          color: var(--gray-800);
          vertical-align: middle;
        }
        .wel-td--secondary { color: var(--gray-500); }
        .wel-td--actions { text-align: center; }

        /* Name cell */
        .wel-name-cell { display: flex; align-items: center; gap: 10px; }
        .wel-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: var(--sky-light);
          color: var(--sky);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .wel-name-info { display: flex; flex-direction: column; min-width: 0; }
        .wel-name { font-weight: 600; color: var(--navy); white-space: nowrap; }
        .wel-email-sub { font-size: 12px; color: var(--gray-400); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }

        /* Status badges */
        .wel-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
        }
        .wel-badge--green  { background: var(--green-light);  color: var(--green);  }
        .wel-badge--sky    { background: var(--sky-light);    color: var(--sky);    }
        .wel-badge--amber  { background: var(--amber-light);  color: #b07800;       }
        .wel-badge--red    { background: #fde8e8;             color: var(--red);    }
        .wel-badge--gray   { background: var(--gray-100);     color: var(--gray-500); }

        /* Icon action buttons */
        .wel-icon-btn {
          background: none;
          border: 1px solid var(--gray-200);
          border-radius: 6px;
          padding: 5px 7px;
          cursor: pointer;
          color: var(--gray-500);
          display: inline-flex;
          align-items: center;
          transition: color 0.15s, border-color 0.15s, background 0.15s;
          margin: 0 2px;
        }
        .wel-icon-btn:hover { color: var(--navy); border-color: var(--navy); background: var(--gray-50); }

        /* Loading / empty */
        .wel-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 60px 20px;
          color: var(--gray-400);
          font-size: 14px;
        }
        .wel-spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .wel-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 60px 20px;
          text-align: center;
        }
        .wel-empty-title { font-size: 15px; font-weight: 600; color: var(--gray-700); margin: 0; }
        .wel-empty-sub   { font-size: 13px; color: var(--gray-400); margin: 0; }

        /* Pagination */
        .wel-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .wel-pagination-left { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .wel-pagination-info { font-size: 13px; color: var(--gray-500); }

        /* Per-page selector */
        .wel-per-page-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--gray-500);
        }
        .wel-per-page-select {
          padding: 4px 8px;
          font-size: 13px;
          border: 1px solid var(--gray-200);
          border-radius: 6px;
          color: var(--gray-800);
          background: #fff;
          cursor: pointer;
          outline: none;
        }
        .wel-per-page-select:focus { border-color: var(--navy); }

        /* Page number buttons */
        .wel-pagination-controls { display: flex; align-items: center; gap: 4px; }
        .wel-page-arrow { padding: 0 6px; height: 32px; display: inline-flex; align-items: center; }
        .wel-page-btn {
          min-width: 32px;
          height: 32px;
          padding: 0 6px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid var(--gray-200);
          border-radius: 6px;
          background: #fff;
          color: var(--gray-700);
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .wel-page-btn:hover { border-color: var(--navy); color: var(--navy); }
        .wel-page-btn--active {
          background: var(--navy);
          border-color: var(--navy);
          color: #fff;
          font-weight: 700;
        }
        .wel-page-ellipsis {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          font-size: 13px;
          color: var(--gray-400);
        }

        /* Modal overlay */
        .wel-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .wel-modal {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          width: 100%;
          max-width: 480px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: welSlideIn 0.18s ease;
        }
        .wel-modal--wide { max-width: 700px; }
        @keyframes welSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wel-modal-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 20px 24px 16px;
          border-bottom: 1px solid var(--gray-100);
          flex-shrink: 0;
        }
        .wel-modal-title { font-size: 16px; font-weight: 700; color: var(--navy); margin: 0; }
        .wel-modal-subtitle { font-size: 12px; color: var(--gray-400); margin: 4px 0 0; }
        .wel-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--gray-400);
          padding: 2px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin-left: 12px;
          transition: color 0.15s;
        }
        .wel-modal-close:hover { color: var(--navy); }
        .wel-modal-body {
          padding: 20px 24px;
          overflow-y: auto;
          flex: 1;
        }

        /* Details modal grid */
        .wel-detail-grid { display: flex; flex-direction: column; gap: 12px; }
        .wel-detail-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .wel-detail-row--full { flex-direction: column; gap: 4px; }
        .wel-detail-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--gray-500);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          flex-shrink: 0;
        }
        .wel-detail-value {
          font-size: 13.5px;
          color: var(--gray-800);
          text-align: right;
          word-break: break-all;
        }
        .wel-detail-value--danger { color: var(--red); text-align: left; }
        .wel-detail-value--mono { font-family: monospace; font-size: 12px; color: var(--gray-600); text-align: left; }

        /* Email body preview */
        .wel-email-preview {
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 16px;
          font-size: 13.5px;
          line-height: 1.6;
          color: var(--gray-800);
          background: var(--gray-50);
          overflow-x: auto;
          max-height: 500px;
          overflow-y: auto;
        }
        .wel-email-preview img { max-width: 100%; }
        .wel-email-preview a { color: var(--sky); }
      `}</style>
    </div>
  );
}
